import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {of} from 'rxjs/internal/observable/of';
import {map, switchMap, toArray} from 'rxjs/operators';
import {concat} from 'rxjs/internal/observable/concat';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    ClientStatusesEnum,
    TradingAccountStatusesEnum,
    TradingService,
    CompaniesService,
    IndividualsService,
    TradingGroupModel,
    TradingClientModel,
    TradingAccountModel,
    CompanyExtendedModel,
    IndividualExtendedModel,
    CompanyStatusTriggerModel,
    IndividualStatusTriggerModel,
    TradingAccountStatusTriggerModel
} from '@spa/api';

import {RadioGroupOptionInterface} from '../../../../shared/components';
import {BaseControl, Helper, RadioGroupControl} from '../../../../shared/classes';

import {ChangeClientStatusFormErrorStatesEnum} from '../../enums';

import {ChangeClientStatusSelector, ClientStatusesComponentControls} from '../../../../../assets/descriptors';

export class ChangeClientStatusForm {
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;
    private readonly tradingClient: TradingClientModel;
    private readonly individualsService: IndividualsService;
    private readonly tradingService: TradingService;
    private readonly companiesService: CompaniesService;
    private readonly isCorporate: boolean;

    private readonly clientStatus: number;

    public formGroup: FormGroup;
    public controls: BaseControl[];

    constructor(
        client: IndividualExtendedModel | CompanyExtendedModel,
        tradingClient: TradingClientModel,
        individualsService: IndividualsService,
        tradingService: TradingService,
        companiesService: CompaniesService,
        isCorporate: boolean
    ) {
        this.client = client;
        this.tradingClient = tradingClient;
        this.individualsService = individualsService;
        this.tradingService = tradingService;
        this.companiesService = companiesService;
        this.isCorporate = isCorporate;

        this.clientStatus = !this.isCorporate ?
            (this.client as IndividualExtendedModel).individualStatus :
            (this.client as CompanyExtendedModel).status;
    }

    private activateTradingAccounts(): Observable<ApiError[]> {
        const requests = this.tradingClient.groups
            .reduce((reducedArray: { isCashAccount: boolean, id: number, reference: string }[], group: TradingGroupModel) => {
                const accountsReducedItems = group.accounts.filter((account: TradingAccountModel) => {
                    return (account.status === TradingAccountStatusesEnum.ForApproval);
                }).map((account: TradingAccountModel) => {
                    return {
                        isCashAccount: account.isCashAccount,
                        id: account.id,
                        reference: account.accountReferenceId
                    };
                });

                reducedArray.push(...accountsReducedItems);

                return reducedArray;
            }, [])
            .sort((
                current: { isCashAccount: boolean, id: null, reference: string },
                next: { isCashAccount: boolean, id: null, reference: string }
            ) => {
                return (current.isCashAccount && !next.isCashAccount) ? -1 : 0;
            })
            .map((reducedArrayItem: { isCashAccount: boolean, id: number, reference: string }) => {
                return this.createTradingAccountStatusRequest(reducedArrayItem.id, reducedArrayItem.reference);
            });

        if (requests.length > 0) {
            return concat(...requests).pipe(
                toArray(),
                map((responses: Array<ApiError | boolean>) => {
                    return responses.reduce((errors: ApiError[], response: ApiError | boolean) => {
                        if (response instanceof ApiError) {
                            errors.push(response);
                        }

                        return errors;
                    }, []);
                })
            );
        }

        const error = new ApiError(ChangeClientStatusFormErrorStatesEnum.CantFindGroupsAndAccountsForActivation);

        return of([error]);
    }

    private createTradingAccountStatusRequest(id: number, reference: string): Observable<ApiError | boolean> {
        const model = new TradingAccountStatusTriggerModel(id, TradingAccountStatusesEnum.Active);

        return this.tradingService.updateTradingAccountStatus(model)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return new ApiError(
                            ChangeClientStatusFormErrorStatesEnum.UpdateTradingAccountStatusFailed,
                            reference
                        );
                    }

                    const account = this.tradingClient.getGroupAccountById(id);

                    account.status = TradingAccountStatusesEnum.Active;

                    return true;
                })
            );
    }

    initForm(statusTriggers: number[]): void {
        this.formGroup = new FormGroup({});

        this.controls = ClientStatusesComponentControls().map((control: BaseControl) => {
            if (control.name === ChangeClientStatusSelector) {
                (control as RadioGroupControl).options.map((option: RadioGroupOptionInterface) => {
                    const triggerIndex = statusTriggers.indexOf(option.value as number);
                    const clientStatusByTrigger = Helper.enumsHelper().getClientStatusByTrigger(option.value as number);

                    if (triggerIndex < 0) {
                        option.disabled = true;
                    }

                    if (clientStatusByTrigger === this.clientStatus) {
                        option.checked = true;

                        control.value = option.value;
                    }
                });

            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    validateForm(
        messagesFunction: (controlsLabel?: string) => { [key: string]: string },
        differentStatusCheckFailedMessageKey: string
    ): boolean {
        const statusesCheck = this.controls.map((control: BaseControl) => {
            const formControl = this.formGroup.controls[control.name];

            if (formControl && formControl.invalid) {
                control.errors = Object.keys(formControl.errors).map((key: string) => {
                    return messagesFunction(control.placeholder)[key];
                });

                return false;
            }

            const accountStatus = Helper.enumsHelper().getClientStatusTriggerByStatus(this.clientStatus);

            if ((control.name === ChangeClientStatusSelector) && (accountStatus === control.value)) {
                control.setError(messagesFunction()[differentStatusCheckFailedMessageKey]);

                return false;
            }

            return true;
        });

        return statusesCheck.indexOf(false) < 0;
    }

    createRequest(
        authUserPermissions: string[],
        changeStatusToTerminatedPermissions: string[]
    ): Observable<ApiError[]> {
        const statusTrigger = this.formGroup.value[ChangeClientStatusSelector];
        const currentStatus = this.clientStatus;
        const newStatus = Helper.enumsHelper().getClientStatusByTrigger(statusTrigger);

        if (
            (newStatus === ClientStatusesEnum.Terminated) &&
            !Helper.getUnionStrategyPermissionsCheck(changeStatusToTerminatedPermissions, authUserPermissions)
        ) {
            const error = new ApiError(ChangeClientStatusFormErrorStatesEnum.ChangeStatusToTerminatedRestricted);

            return of([error]);
        }

        const model = !this.isCorporate ?
            new IndividualStatusTriggerModel((this.client as IndividualExtendedModel).id, statusTrigger) :
            new CompanyStatusTriggerModel((this.client as CompanyExtendedModel).id, statusTrigger);

        const request = !this.isCorporate
            ? this.individualsService.updateStatus(model as IndividualStatusTriggerModel)
            : this.companiesService.updateStatus(model as CompanyStatusTriggerModel);

        return request.pipe(
            switchMap((response: ApiResponse<ApiError[] | null>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    if (response.data && response.data.length > 0) {
                        return of(response.data);
                    }

                    const error = new ApiError(ChangeClientStatusFormErrorStatesEnum.UpdateClientStatusFailed);

                    return of([error]);
                }

                if (this.client instanceof IndividualExtendedModel) {
                    this.client.individualStatus = newStatus;
                } else {
                    this.client.status = newStatus;
                }

                //Accounts are activated on BE with this request

                return of([]);
            })
        );
    }
}
